import { Fragment, useEffect, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Modal } from '@/components/Modal'
import { InfoPopover } from '@/components/InfoPopover'
import { SegmentedToggle } from './SegmentedToggle'
import { MONO, fmt } from './format'
import type { RevenueCategory, RevenueDepartment, RevenueMeasures, RevenueUnit } from '../types'

export interface DrillContext {
  unit: RevenueUnit
  department: RevenueDepartment
  /** Present when the click came from a category row (→ products). */
  category?: RevenueCategory
}

interface DrilldownModalProps {
  open: boolean
  onClose: () => void
  context: DrillContext | null
  month: string
  /** Index of `month` within monthsInYtd — selects the real per-node figure. */
  monthIndex: number
  /** Invoiced-month lens (Inv) vs Order-month lens (Ord). */
  recognition: boolean
  currency: string
  measureLabel: string
}

const DEPT_VIEWS = ['Discipline', 'Stage'] as const
const CAT_VIEWS = ['Products', 'Stage'] as const

/** Drill-down for a clicked revenue cell. From a department: that month by
 *  category (Planning Center adds a Discipline | Stage toggle). From a
 *  category: the products inside it (Ord YTD), with the same Stage toggle for
 *  Planning Center. A category row inside the department view opens its
 *  products without leaving the modal. */
export function DrilldownModal({ open, onClose, context, month, monthIndex, recognition, currency, measureLabel }: DrilldownModalProps) {
  // Inner navigation: a category chosen from the department view.
  const [innerCategoryId, setInnerCategoryId] = useState<string | null>(null)
  const [deptView, setDeptView] = useState<(typeof DEPT_VIEWS)[number]>('Discipline')
  const [catView, setCatView] = useState<(typeof CAT_VIEWS)[number]>('Products')
  const [otherOpen, setOtherOpen] = useState(false)
  useEffect(() => {
    setInnerCategoryId(null)
    setDeptView('Discipline')
    setCatView('Products')
    setOtherOpen(false)
  }, [context, month])

  if (!context) return null
  const { unit, department } = context
  const category = context.category ?? (innerCategoryId ? department.categories.find((c) => c.id === innerCategoryId) : undefined)
  const fromDepartment = !context.category

  const at = (n: RevenueMeasures) => (recognition ? n.inv : n.ord)[monthIndex] ?? 0
  const ytd = (n: RevenueMeasures) => (recognition ? n.invYtd : n.ordYtd)
  const share = (v: number, total: number) => (total ? Math.round((v / total) * 100) : 0)

  // ------------------------------------------------------------------ category → products
  if (category) {
    const stages = category.stages
    const monthValue = at(category)
    const ytdValue = ytd(category)
    const products = category.products
    const other = products.find((p) => p.other)
    const named = products.filter((p) => !p.other)
    const productsYtd = products.reduce((t, p) => t + p.ordYtd, 0)
    const rows =
      catView === 'Stage' && stages
        ? stages.map((s) => ({ key: s.id, name: s.name, month: at(s), ytd: ytd(s) }))
        : null

    return (
      <Modal
        open={open}
        onClose={onClose}
        eyebrow={`${unit.name} › ${department.name} › ${category.name} · ${month}`}
        title="Category → Product"
        subtitle={
          <>
            {measureLabel} · <span className={MONO}>{fmt(monthValue, currency)}</span> this month · YTD{' '}
            <span className={MONO}>{fmt(ytdValue, currency)}</span>
            <InfoPopover term={`category:${category.id}`} />
          </>
        }
        className="max-w-2xl"
        footer={
          rows
            ? 'Planning Center stage split — Planning & Motivating vs Execution — for this month and YTD, on the selected lens.'
            : 'Products carry True Sale (Ord) year-to-date on the order month; a per-month and invoiced figure per product arrives with the October export. The list is the top products by YTD plus one "Other" line, and it sums to the category.'
        }
      >
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {fromDepartment ? (
            <button
              type="button"
              onClick={() => setInnerCategoryId(null)}
              className="inline-flex items-center gap-1 text-xs font-medium text-champagne-700 hover:text-champagne-800 dark:text-champagne-300 dark:hover:text-champagne-200"
            >
              ← Back to {department.name}
            </button>
          ) : (
            <span />
          )}
          {stages ? <SegmentedToggle options={CAT_VIEWS} value={catView} onChange={setCatView} size="sm" ariaLabel="Products or stage" /> : null}
        </div>

        {rows ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                <th className="pb-2 pr-3 text-left font-medium">Stage</th>
                <th className="pb-2 pl-3 text-right font-medium">This month</th>
                <th className="pb-2 pl-3 text-right font-medium">YTD</th>
                <th className="pb-2 pl-3 text-right font-medium">Share</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-stone-50 dark:border-stone-800/60">
                  <td className="py-2.5 pr-3 font-medium text-stone-800 dark:text-stone-100">{r.name}</td>
                  <td className={cn('py-2.5 pl-3 text-right', MONO)}>{fmt(r.month, currency)}</td>
                  <td className={cn('py-2.5 pl-3 text-right text-stone-500 dark:text-stone-400', MONO)}>{fmt(r.ytd, currency)}</td>
                  <td className={cn('py-2.5 pl-3 text-right text-xs text-stone-400', MONO)}>{share(r.ytd, ytdValue)}%</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-300 dark:border-stone-600">
                <td className="pt-3 font-semibold">Total</td>
                <td className={cn('pt-3 pl-3 text-right font-bold', MONO)}>{fmt(monthValue, currency)}</td>
                <td className={cn('pt-3 pl-3 text-right font-semibold text-stone-500', MONO)}>{fmt(ytdValue, currency)}</td>
                <td />
              </tr>
            </tfoot>
          </table>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
                <th className="pb-2 pr-3 text-left font-medium">Product</th>
                {stages ? <th className="hidden pb-2 pr-3 text-left font-medium sm:table-cell">Stage</th> : null}
                <th className="pb-2 pl-3 text-right font-medium">Ord YTD</th>
                <th className="hidden pb-2 pl-3 text-right font-medium sm:table-cell">Share</th>
              </tr>
            </thead>
            <tbody>
              {named.map((p) => (
                <tr key={p.name} className="border-b border-stone-50 dark:border-stone-800/60">
                  <td className="py-2 pr-3 text-stone-800 dark:text-stone-100">
                    <span className="block" title={p.legacyClass ? `was: ${p.legacyClass}` : undefined}>{p.name}</span>
                  </td>
                  {stages ? (
                    <td className="hidden py-2 pr-3 text-xs text-stone-500 sm:table-cell dark:text-stone-400">
                      {stages.find((s) => s.id === p.stage)?.name ?? '—'}
                    </td>
                  ) : null}
                  <td className={cn('py-2 pl-3 text-right', MONO)}>{fmt(p.ordYtd, currency)}</td>
                  <td className={cn('hidden py-2 pl-3 text-right text-xs text-stone-400 sm:table-cell', MONO)}>{share(p.ordYtd, productsYtd)}%</td>
                </tr>
              ))}
              {other ? (
                <Fragment>
                  <tr className="border-b border-stone-50 dark:border-stone-800/60">
                    <td className="py-2 pr-3 text-stone-700 dark:text-stone-200" colSpan={stages ? 2 : 1}>
                      <button
                        type="button"
                        onClick={() => setOtherOpen((o) => !o)}
                        aria-expanded={otherOpen}
                        className="inline-flex items-center gap-1.5 text-left transition-colors hover:text-stone-900 dark:hover:text-white"
                      >
                        <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-stone-400 transition-transform', otherOpen && 'rotate-90')} />
                        {other.name}
                      </button>
                    </td>
                    <td className={cn('py-2 pl-3 text-right', MONO)}>{fmt(other.ordYtd, currency)}</td>
                    <td className={cn('hidden py-2 pl-3 text-right text-xs text-stone-400 sm:table-cell', MONO)}>{share(other.ordYtd, productsYtd)}%</td>
                  </tr>
                  {otherOpen ? (
                    <tr className="bg-stone-50/60 dark:bg-stone-800/30">
                      <td className="py-2 pl-6 pr-3 text-xs text-stone-500 dark:text-stone-400" colSpan={stages ? 4 : 3}>
                        {(other.products ?? []).join(' · ')}
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ) : null}
              {!products.length ? (
                <tr>
                  <td className="py-4 text-xs text-stone-400" colSpan={stages ? 4 : 3}>
                    No website products in this category this year.
                  </td>
                </tr>
              ) : null}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-stone-300 dark:border-stone-600">
                <td className="pt-3 font-semibold" colSpan={stages ? 2 : 1}>
                  Total · {category.productCount} {category.productCount === 1 ? 'product' : 'products'}
                </td>
                <td className={cn('pt-3 pl-3 text-right font-bold', MONO)}>{fmt(productsYtd, currency)}</td>
                <td className="hidden sm:table-cell" />
              </tr>
            </tfoot>
          </table>
        )}
      </Modal>
    )
  }

  // ------------------------------------------------------------------ department → categories
  const stages = department.stages
  const monthValue = at(department)
  const ytdValue = ytd(department)
  const byStage = deptView === 'Stage' && stages
  const rows = byStage
    ? stages.map((s) => ({ key: s.id, name: s.name, month: at(s), ytd: ytd(s), categoryId: null as string | null }))
    : department.categories.map((c) => ({ key: c.id, name: c.name, month: at(c), ytd: ytd(c), categoryId: c.id as string | null }))

  return (
    <Modal
      open={open}
      onClose={onClose}
      eyebrow={`${unit.name} › ${department.name} · ${month}`}
      title="Department → Category"
      subtitle={
        <>
          {measureLabel} · <span className={MONO}>{fmt(monthValue, currency)}</span> this month · YTD{' '}
          <span className={MONO}>{fmt(ytdValue, currency)}</span>
        </>
      }
      footer={
        byStage
          ? 'Planning Center by stage — Planning & Motivating (planning, diagnostics, motivational design) vs Execution (guides, restorations, aligners). Same total as the discipline view.'
          : recognition
            ? 'Revenue (Inv) by category, on the invoice-date month. Click a category for its products.'
            : 'Ordered True Sale by category, on the order-created month. Click a category for its products.'
      }
    >
      {stages ? (
        <div className="mb-3 flex items-center justify-end">
          <SegmentedToggle options={DEPT_VIEWS} value={deptView} onChange={setDeptView} size="sm" ariaLabel="Discipline or stage" />
          <InfoPopover term="stage" className="ml-1" />
        </div>
      ) : null}
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-200 text-xs uppercase tracking-wide text-stone-400 dark:border-stone-700 dark:text-stone-500">
            <th className="pb-2 pr-3 text-left font-medium">{byStage ? 'Stage' : 'Category'}</th>
            <th className="pb-2 pl-3 text-right font-medium">This month</th>
            <th className="pb-2 pl-3 text-right font-medium">YTD</th>
            <th className="hidden pb-2 pl-3 text-right font-medium sm:table-cell">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-b border-stone-50 dark:border-stone-800/60">
              <td className="py-2.5 pr-3 font-medium text-stone-800 dark:text-stone-100">
                {r.categoryId ? (
                  <button
                    type="button"
                    onClick={() => setInnerCategoryId(r.categoryId)}
                    className="inline-flex items-center gap-1.5 text-left transition-colors hover:text-champagne-700 dark:hover:text-champagne-300"
                    title="See the products in this category"
                  >
                    {r.name}
                    <ChevronRight className="h-3.5 w-3.5 text-stone-400" />
                  </button>
                ) : (
                  r.name
                )}
              </td>
              <td className={cn('py-2.5 pl-3 text-right', MONO)}>{fmt(r.month, currency)}</td>
              <td className={cn('py-2.5 pl-3 text-right text-stone-500 dark:text-stone-400', MONO)}>{fmt(r.ytd, currency)}</td>
              <td className={cn('hidden py-2.5 pl-3 text-right text-xs text-stone-400 sm:table-cell', MONO)}>{share(r.ytd, ytdValue)}%</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-stone-300 dark:border-stone-600">
            <td className="pt-3 font-semibold">Total</td>
            <td className={cn('pt-3 pl-3 text-right font-bold', MONO)}>{fmt(monthValue, currency)}</td>
            <td className={cn('pt-3 pl-3 text-right font-semibold text-stone-500', MONO)}>{fmt(ytdValue, currency)}</td>
            <td className="hidden sm:table-cell" />
          </tr>
        </tfoot>
      </table>
    </Modal>
  )
}
